import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  useTheme
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import clsx from 'clsx'
import { CirclePlus, MessagesSquare, UsersRound } from 'lucide-react'
import { useEffect } from 'react'

import { ChatModal } from '../../components/ChatModal'
import TableRowSkeleton from '../../components/TableRowSkeleton'
import { useChatLogic } from '../../hooks/useChatLogic'
import ChatDrawerMessages from '../ChatDrawerMessages'
import { InputFieldText } from '../InputFieldText'
import { useStyles } from './style'; // Reutilizando seus estilos

const ChatMobile = ({
  initialChatType = null,
  initialChatData = null,
  onClose,
  onShowMessages
}) => {
  const classes = useStyles()
  const theme = useTheme()
  // Usamos a mesma lógica do hook, garantindo consistência
  const {
    tabValue,
    showDialog,
    selectedUser,
    selectedChat,
    showMessages,
    loading,
    // hasMore,
    searchParam,
    users,
    chats,
    loadedAvatars,
    avatarLoadingStates,
    usersStatus,
    currentUser,
    actions,
    helpers
  } = useChatLogic(true, initialChatType, initialChatData)

  const handleTabChange = (event, newValue) =>
    actions.handleTabChange(event, newValue)
  const handleSearch = event => actions.handleSearch(event)
  const handleBackToList = () => actions.handleBackToList()
  const handleNewGroup = () => actions.setShowDialog(true)

  const handleLoadNewChat = data => {
    actions.setSelectedChat(data)
    actions.setSelectedUser(null)
    actions.setShowMessages(true)
    actions.chatDispatch({ type: 'UPDATE_ITEM', payload: data })
  }

  useEffect(() => {
    if (onShowMessages) {
      onShowMessages(showMessages)
    }
  }, [showMessages, onShowMessages])

  // --- Helpers de UI ---
  const getInitials = name => {
    return name
      ? name
          .split(' ')
          .map(n => n.charAt(0).toUpperCase())
          .join('')
          .substring(0, 2)
      : '?'
  }

  const getAvatarClass = name => {
    if (!name) return classes.purple
    const initial = name.charAt(0).toLowerCase()
    if (initial >= 'a' && initial <= 'c') return classes.grey
    if (initial >= 'd' && initial <= 'f') return classes.blue
    if (initial >= 'g' && initial <= 'l') return classes.cyan
    return classes.purple
  }

  const StatusBadge = ({ isOnline }) => (
    <div
      className={clsx(
        classes.statusBadge,
        isOnline ? classes.onlineBadge : classes.offlineBadge
      )}
    />
  )

  const UnreadCounter = ({ count }) => {
    if (count <= 0) return null
    return <Chip label={count} className={classes.badgeCountMessage} />
  }

  const IntegratedAvatar = ({ user, className, avatarClass, onClick }) => {
    const displayName = user?.name || user?.title || ''
    const userId = user?.id
    const avatarData = loadedAvatars[userId]

    useEffect(() => {
      if (user?.title) return
      if (userId && !avatarData && !avatarLoadingStates[userId]) {
        actions.loadUserAvatar(userId)
      }
    }, [userId, avatarData, user?.title])

    if (user?.title) {
      return (
        <Avatar
          className={`${className} ${avatarClass || ''}`}
          onClick={onClick}
        >
          {getInitials(displayName)}
        </Avatar>
      )
    }

    if (avatarData?.loaded && avatarData?.url && !avatarData?.error) {
      return (
        <Avatar
          className={classes.avatar}
          src={avatarData.url}
          alt={displayName}
          onClick={onClick}
          onError={() => actions.handleAvatarError(userId)}
        />
      )
    }

    return (
      <Avatar className={`${className} ${avatarClass || ''}`} onClick={onClick}>
        {getInitials(displayName)}
      </Avatar>
    )
  }

  if (showMessages) {
    return (
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1250,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <ChatDrawerMessages
          user={selectedUser}
          chat={selectedChat}
          onClose={handleBackToList}
        />
      </Box>
    )
  }

  return (
    <Grid container direction="column">
      <ChatModal
        open={showDialog}
        chat={selectedChat}
        handleLoadNewChat={handleLoadNewChat}
        handleClose={() => actions.setShowDialog(false)}
        user={currentUser}
      />

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        className={classes.tab}
      >
        <Tab icon={<MessagesSquare size={26} />} label="Team" />
        <Tab icon={<UsersRound size={26} />} label="Grupos" />
      </Tabs>

      {/* Busca */}
      <div className={classes.form}>
        <div
          className={clsx(
            classes.searchContainer,
            tabValue === 1 &&
              currentUser?.profile === 'admin' &&
              classes.searchContainerWithButton
          )}
        >
          <InputFieldText
            fullWidth // Importante para ocupar o container
            size="small"
            name={'searchParam'}
            placeholder="Pesquisar..."
            onChange={handleSearch}
            value={searchParam}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" className={classes.searchIcon}>
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </div>

        {/* Botão de Novo Grupo - Renderiza apenas na aba 1 (Grupos) */}
        {tabValue === 1 && currentUser?.profile === 'admin' && (
          <div className={classes.newGroupButtonContainer}>
            <Button
              variant="contained"
              onClick={handleNewGroup}
              className={classes.newGroupButton}
            >
              <CirclePlus size={28} />
            </Button>
          </div>
        )}
      </div>

      {/* Lista de Contatos/Grupos */}
      <List className={classes.chatList}>
        {(tabValue === 0 ? users : chats).map(item => {
          const unreads = helpers.getUnreadCount(item)
          const hasUnread = unreads > 0
          const isUser = tabValue === 0
          const isOnline = !!usersStatus[String(item.id)]

          return (
            <ListItem
              button
              key={item.id}
              className={`${classes.userItem} ${hasUnread ? classes.unreadItem : ''}`}
              onClick={() =>
                isUser
                  ? actions.handleOpenChat(item)
                  : actions.handleOpenGroupChat(item)
              }
            >
              <div style={{ position: 'relative' }}>
                <IntegratedAvatar
                  user={item}
                  className={classes.avatar}
                  avatarClass={getAvatarClass(item.name || item.title)}
                />
                {isUser && unreads === 0 && <StatusBadge isOnline={isOnline} />}
              </div>
              <UnreadCounter count={unreads} />
              <ListItemText
                className={classes.userItemText}
                primary={
                  <div className={classes.boxTitleNewChat}>
                    <span
                      className={`${classes.titleNewChat} ${hasUnread ? classes.titleNewChatBold : ''}`}
                    >
                      {item.name || item.title}
                    </span>
                  </div>
                }
                secondary={
                  item.lastMessage ? (
                    <span className={classes.lastMessage}>
                      {item.lastMessage}
                    </span>
                  ) : null
                }
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
          )
        })}
        {loading && (
          <div className={classes.loadingContainer}>
            <TableRowSkeleton columns={1} />
          </div>
        )}
      </List>
    </Grid>
  )
}

export default ChatMobile
